import { parseAxiosError } from "@/helpers/parseAxiosError"
import {
  ISkillEffectEntry,
  ISkillEntry
} from "@/store/apis/catalogsAPI/catalogs.types"
import {
  IAttributeAllocation,
  IHeroAttribute,
  IHeroLevel,
  IHeroSkill,
  ISkillAllocation
} from "@/store/apis/heroAPI/hero.types"

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

export const xpBarFill = (level: IHeroLevel): number => {
  if (level.xpForNext === null || level.xpForNext <= level.xpForCurrent) {
    return 1
  }
  return clamp01(
    (level.xp - level.xpForCurrent) / (level.xpForNext - level.xpForCurrent)
  )
}

export const xpBarLabel = (level: IHeroLevel): string | null => {
  if (level.xpForNext === null) {
    return null
  }
  return `${level.xp - level.xpForCurrent} / ${level.xpForNext - level.xpForCurrent}`
}

export const levelLabel = (level: IHeroLevel): string =>
  level.minor > 0 ? `${level.major}.${level.minor}` : `${level.major}`

/** Staged, uncommitted points per code; cleared on a successful allocation, after which the screen re-reads the server. */
export type IAllocationDraft = Record<string, number>

export const draftTotal = (draft: IAllocationDraft): number =>
  Object.values(draft).reduce((sum, points) => sum + points, 0)

export const remainingBank = (bank: number, draft: IAllocationDraft): number =>
  bank - draftTotal(draft)

/** `enabled: false` blocks staging on a locked code; `ceiling` is the most points stageable on it (Infinity for an attribute, maxRank − rank for a skill). */
export interface IDeltaLimits {
  enabled: boolean
  ceiling: number
}

/** The single mutation point for the draft; an illegal move returns the same object, so callers compare by identity. */
export const applyDelta = (
  draft: IAllocationDraft,
  code: string,
  delta: number,
  bank: number,
  limits: IDeltaLimits
): IAllocationDraft => {
  const staged = draft[code] ?? 0
  if (
    delta > 0 &&
    (!limits.enabled ||
      remainingBank(bank, draft) < delta ||
      staged + delta > limits.ceiling)
  ) {
    return draft
  }
  const rollsBackSpentPoints = delta < 0 && staged + delta < 0
  if (rollsBackSpentPoints) {
    return draft
  }
  const next = { ...draft }
  const value = staged + delta
  if (value === 0) {
    delete next[code]
  } else {
    next[code] = value
  }
  return next
}

export const draftToAllocations = (
  draft: IAllocationDraft
): IAttributeAllocation[] =>
  Object.entries(draft)
    .filter(([, points]) => points > 0)
    .map(([statCode, points]) => ({ statCode, points }))

export interface IAttributeRow {
  code: string
  base: number
  staged: number
  gearBonus: number
  available: boolean
}

export const buildAttributeRows = (
  attributes: IHeroAttribute[],
  draft: IAllocationDraft
): IAttributeRow[] =>
  attributes.map((attribute) => ({
    code: attribute.code,
    base: attribute.base,
    staged: draft[attribute.code] ?? 0,
    gearBonus: attribute.effective - attribute.base,
    available: attribute.available
  }))

export interface ISkillRow {
  code: string
  rank: number
  maxRank: number
  staged: number
  effects: ISkillEffectEntry[]
}

/** The catalog owns the order and the ceiling; a code the hero payload omits shows at rank 0. An empty catalog or a missing payload yields no rows. */
export const buildSkillRows = (
  catalog: ISkillEntry[],
  skills: IHeroSkill[],
  draft: IAllocationDraft
): ISkillRow[] => {
  if (catalog.length === 0 || !skills) {
    return []
  }
  const rankByCode = new Map(skills.map((skill) => [skill.code, skill.rank]))
  return [...catalog]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((entry) => ({
      code: entry.code,
      rank: rankByCode.get(entry.code) ?? 0,
      maxRank: entry.maxRank,
      staged: draft[entry.code] ?? 0,
      effects: entry.effects ?? []
    }))
}

export const draftToSkillAllocations = (
  draft: IAllocationDraft
): ISkillAllocation[] =>
  Object.entries(draft)
    .filter(([, ranks]) => ranks > 0)
    .map(([skillCode, ranks]) => ({ skillCode, ranks }))

type IAllocationScreen = "attributes" | "skills"

/** The backend's insufficient-bank 409 texts, matched exactly: any other 409 reads as a generic conflict. */
export const INSUFFICIENT_BANK_MESSAGES: Record<IAllocationScreen, string> = {
  attributes: "Not enough attribute points in the bank for this allocation",
  skills: "Not enough skill points in the bank for this allocation"
}

const ALLOCATION_ERROR_KEYS: Record<
  IAllocationScreen,
  {
    insufficient: string
    conflict: string
    byStatus: Record<number, string>
    fallback: string
  }
> = {
  attributes: {
    insufficient: "allocate.error.insufficient",
    conflict: "allocate.error.conflict",
    byStatus: {
      400: "allocate.error.invalid",
      422: "allocate.error.unavailable"
    },
    fallback: "allocate.error.failed"
  },
  skills: {
    insufficient: "skills.error.insufficient",
    conflict: "skills.error.conflict",
    byStatus: { 400: "skills.error.invalid", 422: "skills.error.ceiling" },
    fallback: "skills.error.failed"
  }
}

export const allocationErrorKey = (
  error: unknown,
  screen: IAllocationScreen
): string => {
  const { statusCode, message } = parseAxiosError(error)
  const keys = ALLOCATION_ERROR_KEYS[screen]
  if (statusCode === 409) {
    return message === INSUFFICIENT_BANK_MESSAGES[screen]
      ? keys.insufficient
      : keys.conflict
  }
  return keys.byStatus[statusCode] ?? keys.fallback
}
